import { useQuery } from "@tanstack/react-query";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import { pickEndpoint } from "~/shared/services/api/pick-endpoint.ts";
import { extractApiError } from "~/shared/utils/extract-api-error.ts";

import type {
	GridPreference,
	UserPreferences,
} from "../schemas/user-preferences-schema.ts";
import { userPreferencesSchema } from "../schemas/user-preferences-schema.ts";

export const USER_PREFERENCES_QUERY_KEY = ["user-preferences"] as const;

/**
 * Builds the exact query key the user-preferences query is cached under.
 * Any code that writes to this cache with `setQueryData` must use this exact
 * key — `setQueryData` matches by exact key.
 */
export const getUserPreferencesQueryKey = () => USER_PREFERENCES_QUERY_KEY;

const getUserPreferences = async (url: string): Promise<UserPreferences> => {
	const response = await getAndValidate(url, userPreferencesSchema);
	if (!response.success) {
		const apiError = await extractApiError(response.error);
		throw new Error(apiError?.error ?? "Failed to fetch user preferences");
	}

	return response.data as UserPreferences;
};

type UseGetUserPreferencesResult<T> = {
	data: T | undefined;
	isLoading: boolean;
	error: Error | null;
};

export function useGetUserPreferences(): UseGetUserPreferencesResult<UserPreferences>;
export function useGetUserPreferences(
	gridId: number,
): UseGetUserPreferencesResult<GridPreference>;
export function useGetUserPreferences(
	gridId?: number,
): UseGetUserPreferencesResult<UserPreferences | GridPreference> {
	const { isInternal } = useDeploymentType();
	const { isVendor, isLoading: isVendorLoading } = useIsVendor();

	const url = pickEndpoint(
		{
			internal: apiRoutes.INTERNALUSERPREFERENCES,
			external: apiRoutes.USERPREFERENCES,
			vendor: apiRoutes.VENDORUSERPREFERENCES,
		},
		{ isInternal, isVendor },
	);

	const query = useQuery({
		queryKey: getUserPreferencesQueryKey(),
		queryFn: () => getUserPreferences(url),
		retry: false,
		// Once the query has an answer (data or error), don't refetch or retry
		// when a new subscriber mounts. Without this, an errored query (e.g. a
		// vendor user hitting 403) flips status `error` -> `pending` on every
		// new subscribe, which flips `isLoading` to true and causes any
		// `isLoading`-gated parent provider to swap children for a spinner --
		// producing a remount/refetch loop visible as page flicker.
		refetchOnMount: false,
		retryOnMount: false,
		// User column preferences are only persisted for external and vendor
		// users. Vendors read/write their own dedicated endpoint
		// (`VENDORUSERPREFERENCES`), so skip the request only for internal users.
		// Additionally wait for vendor-role resolution before enabling so we
		// never fire the request against the wrong endpoint — `useIsVendor`
		// defaults to `isVendor: true` while loading, which would cause a
		// non-vendor to hit `VENDORUSERPREFERENCES` and receive a 403.
		enabled: !isInternal && !isVendorLoading,
	});

	return {
		data: gridId === undefined ? query.data : query.data?.[String(gridId)],
		isLoading: isVendorLoading || query.isLoading,
		error: query.error,
	};
}
